import React, { useState, useEffect } from 'react';
import { Trophy, Zap, Users, Star, Clock, Gift, Gamepad2, ChevronRight, Award, Gem, ArrowUp, BarChart, Coins, Check } from 'lucide-react';
import { useUI } from '../contexts/UIContext';
import { useAuth } from '../contexts/AuthContext';
import BottomNavigation from '../components/BottomNavigation';
import { t } from '../i18n';

/**
 * GameHub Ekranı
 * Kullanıcıların oyunlar oynayabildiği, ödüller kazanabildiği ve liderlik tablolarını görüntüleyebildiği ekran
 */
const GameHubScreen = () => {
  const { navigateToScreen } = useUI();
  const { user } = useAuth();
  
  // Durum değişkenleri
  const [activeTab, setActiveTab] = useState('games');
  const [isLoading, setIsLoading] = useState(true);
  const [games, setGames] = useState([]);
  const [dailyTasks, setDailyTasks] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [userStats, setUserStats] = useState(null);
  const [showGameDetail, setShowGameDetail] = useState(null);
  const [activeGameId, setActiveGameId] = useState(null);

  // Demo verileri yükle
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        // API isteği simülasyonu
        await new Promise(resolve => setTimeout(resolve, 800));
        
        // Oyunlar
        const demoGames = [
          {
            id: 1,
            title: t('gameHub.games.cryptoQuest.title'),
            description: t('gameHub.games.cryptoQuest.description'),
            image: "crypto-quest.jpg",
            category: "quiz",
            players: 3245,
            difficulty: "medium",
            rewards: {
              daily: 5,
              completion: 50,
              tournament: 500
            },
            userProgress: 75,
            totalLevels: 10,
            completedLevels: 7,
            tournamentActive: true,
            tournamentEnds: Date.now() + 86400000 * 2, // 2 gün sonra
            minLevel: 0
          },
          {
            id: 2,
            title: t('gameHub.games.blockchainPuzzle.title'),
            description: t('gameHub.games.blockchainPuzzle.description'),
            image: "blockchain-puzzle.jpg",
            category: "puzzle",
            players: 2189,
            difficulty: "hard",
            rewards: {
              daily: 3,
              completion: 30,
              tournament: 300
            },
            userProgress: 40,
            totalLevels: 25,
            completedLevels: 10,
            tournamentActive: true,
            tournamentEnds: Date.now() + 86400000 * 5, // 5 gün sonra
            minLevel: 0
          },
          {
            id: 3,
            title: t('gameHub.games.nftCollector.title'),
            description: t('gameHub.games.nftCollector.description'),
            image: "nft-collection.jpg",
            category: "collection",
            players: 1567,
            difficulty: "easy",
            rewards: {
              daily: 2,
              completion: 20,
              tournament: 200
            },
            userProgress: 30,
            totalLevels: 15,
            completedLevels: 4,
            tournamentActive: false,
            tournamentEnds: null,
            minLevel: 2
          },
          {
            id: 4,
            title: t('gameHub.games.defiTrader.title'),
            description: t('gameHub.games.defiTrader.description'),
            image: "defi-trader.jpg",
            category: "simulation",
            players: 4231,
            difficulty: "hard",
            rewards: {
              daily: 4,
              completion: 40,
              tournament: 400
            },
            userProgress: 25,
            totalLevels: 20,
            completedLevels: 5,
            tournamentActive: true,
            tournamentEnds: Date.now() + 86400000 * 7, // 7 gün sonra
            minLevel: 3
          },
          {
            id: 5,
            title: t('gameHub.games.daoManager.title'),
            description: t('gameHub.games.daoManager.description'),
            image: "dao-manager.jpg",
            category: "strategy",
            players: 1845,
            difficulty: "medium",
            rewards: {
              daily: 3,
              completion: 35,
              tournament: 350
            },
            userProgress: 10,
            totalLevels: 30,
            completedLevels: 3,
            tournamentActive: false,
            tournamentEnds: null,
            minLevel: 5
          }
        ];

        // Günlük görevler
        const demoDailyTasks = [
          {
            id: 101,
            title: t('gameHub.dailyTasks.completeCryptoQuestLevels'),
            reward: 10,
            isCompleted: true,
            gameId: 1
          },
          {
            id: 102,
            title: t('gameHub.dailyTasks.solveBlockchainPuzzle'),
            reward: 5,
            isCompleted: false,
            gameId: 2
          },
          {
            id: 103,
            title: t('gameHub.dailyTasks.joinCryptoQuestTournament'),
            reward: 15,
            isCompleted: false,
            gameId: 1
          },
          {
            id: 104,
            title: t('gameHub.dailyTasks.collectDailyIdcoin'),
            reward: 3,
            isCompleted: false,
            gameId: null
          },
          {
            id: 105,
            title: t('gameHub.dailyTasks.inviteFriend'),
            reward: 20,
            isCompleted: false,
            gameId: null
          }
        ];

        // Liderlik tablosu
        const demoLeaderboard = [
          {
            rank: 1,
            username: "cryptomaster",
            points: 8750,
            avatar: null,
            isCurrentUser: false
          },
          {
            rank: 2,
            username: "blockchainqueen",
            points: 8340,
            avatar: null,
            isCurrentUser: false
          },
          {
            rank: 3,
            username: "tokenwizard",
            points: 7920,
            avatar: null,
            isCurrentUser: false
          },
          {
            rank: 4,
            username: "nftcollector",
            points: 7750,
            avatar: null,
            isCurrentUser: false
          },
          {
            rank: 5,
            username: "defiking",
            points: 7430,
            avatar: null,
            isCurrentUser: false
          },
          {
            rank: 6,
            username: "satoshifan",
            points: 7120,
            avatar: null,
            isCurrentUser: false
          },
          {
            rank: 7,
            username: "ethdev",
            points: 6980,
            avatar: null,
            isCurrentUser: false
          },
          {
            rank: 8,
            username: "alihan",
            points: 6750,
            avatar: null,
            isCurrentUser: true
          }
        ];

        // Kullanıcı istatistikleri
        const demoUserStats = {
          level: 8,
          xp: 6750,
          nextLevelXp: 7500,
          tokens: 345,
          gamesPlayed: 48,
          achievements: 12,
          dailyStreak: 5,
          rank: 8,
          totalUsers: 12500
        };

        setGames(demoGames);
        setDailyTasks(demoDailyTasks);
        setLeaderboard(demoLeaderboard);
        setUserStats(demoUserStats);
        setIsLoading(false);
      } catch (error) {
        console.error(t('gameHub.errors.loadFailed'), error);
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  // Görev tamamlama
  const toggleTaskComplete = (taskId) => {
    setDailyTasks(prev =>
      prev.map(task =>
        task.id === taskId
          ? { ...task, isCompleted: !task.isCompleted }
          : task
      )
    );

    // Kullanıcı XP ve token güncellemesi
    if (dailyTasks.find(t => t.id === taskId)?.isCompleted === false) {
      const reward = dailyTasks.find(t => t.id === taskId)?.reward || 0;
      setUserStats(prev => ({
        ...prev,
        xp: prev.xp + reward * 10,
        tokens: prev.tokens + reward
      }));
    }
  };

  // Oyuna giriş
  const handlePlayGame = (gameId) => {
    setActiveGameId(gameId);
    // Oyun ayrıntılarını göster
    setShowGameDetail(games.find(g => g.id === gameId));
  };

  // Zorluk seviyesi simgesi
  const getDifficultyIcon = (difficulty) => {
    switch (difficulty) {
      case 'easy':
        return <div className="flex"><Zap size={14} className="text-green-500" /><Zap size={14} className="text-gray-300" /><Zap size={14} className="text-gray-300" /></div>;
      case 'medium':
        return <div className="flex"><Zap size={14} className="text-yellow-500" /><Zap size={14} className="text-yellow-500" /><Zap size={14} className="text-gray-300" /></div>;
      case 'hard':
        return <div className="flex"><Zap size={14} className="text-red-500" /><Zap size={14} className="text-red-500" /><Zap size={14} className="text-red-500" /></div>;
      default:
        return null;
    }
  };

  // Kategori simgesi
  const getCategoryIcon = (category) => {
    switch (category) {
      case 'quiz':
        return <svg xmlns="http://www.w3.org/2000/svg" className="text-blue-500" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>;
      case 'puzzle':
        return <svg xmlns="http://www.w3.org/2000/svg" className="text-purple-500" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4a7 7 0 0 0 0 14h7a3 3 0 0 0 0-6h-1a2 2 0 0 1 0-4h1a3 3 0 0 0 0-6h-7z"></path><path d="M15.5 12.5 17 14l3-3"></path></svg>;
      case 'collection':
        return <svg xmlns="http://www.w3.org/2000/svg" className="text-yellow-500" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>;
      case 'simulation':
        return <svg xmlns="http://www.w3.org/2000/svg" className="text-green-500" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m2 2l20 20"></path><path d="M8.5 8.5c1.7-1.7 4.3-1.7 6 0 1.7 1.7 1.7 4.3 0 6-1.7 1.7-4.3 1.7-6 0-1.7-1.7-1.7-4.3 0-6Z"></path><path d="M14.5 9.5c1.7-1.7 4.3-1.7 6 0 1.7 1.7 1.7 4.3 0 6-1.7 1.7-4.3 1.7-6 0"></path><path d="M3.5 14.5c-1.7 1.7-1.7 4.3 0 6 1.7 1.7 4.3 1.7 6 0 1.7-1.7 1.7-4.3 0-6"></path><path d="M9.5 3.5c-1.7-1.7-4.3-1.7-6 0-1.7 1.7-1.7 4.3 0 6"></path></svg>;
      case 'strategy':
        return <svg xmlns="http://www.w3.org/2000/svg" className="text-red-500" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 19H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-4"></path><path d="M9 14.5 4 9l5-5"></path><path d="M14 8v3h-8"></path></svg>;
      default:
        return <Gamepad2 className="text-gray-500" size={16} />;
    }
  };

  // Turnuva bitiş zamanını formatla
  const formatTournamentEndTime = (timestamp) => {
    if (!timestamp) return null;
    
    const now = Date.now();
    const diff = timestamp - now;
    
    if (diff <= 0) return t('gameHub.tournament.ended');
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) {
      return t('gameHub.tournament.daysHoursLeft', { days, hours });
    } else {
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      return t('gameHub.tournament.hoursMinutesLeft', { hours, minutes });
    }
  };

  // Oyun detay modali
  const GameDetailModal = ({game, onClose}) => {
    if (!game) return null;
    
    const { 
      title, description, category, difficulty, players, 
      rewards, userProgress, totalLevels, completedLevels,
      tournamentActive, tournamentEnds
    } = game;
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-lg w-full max-w-md overflow-hidden">
          <div className="bg-indigo-600 p-4 text-white relative">
            <button 
              className="absolute right-4 top-4 text-white bg-white/20 rounded-full p-1"
              onClick={onClose}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </button>
            <h2 className="text-xl font-bold mb-1">{title}</h2>
            <div className="flex items-center text-xs mb-2">
              <span className="flex items-center mr-3">
                {getCategoryIcon(category)}
                <span className="ml-1">{t(`gameHub.categories.${category}`)}</span>
              </span>
              <span className="flex items-center mr-3">
                {getDifficultyIcon(difficulty)}
              </span>
              <span className="flex items-center">
                <Users size={14} className="mr-1" />
                {players.toLocaleString()}
              </span>
            </div>
            <p className="text-sm">{description}</p>
          </div>
          
          <div className="p-4">
            <div className="mb-4">
              <h3 className="font-medium mb-2">{t('gameHub.progress')}</h3>
              <div className="flex justify-between text-sm mb-1">
                <span>{t('gameHub.level', { completed: completedLevels, total: totalLevels })}</span>
                <span>{userProgress}%</span>
              </div>
              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-indigo-500 rounded-full" 
                  style={{ width: `${userProgress}%` }}
                ></div>
              </div>
            </div>
            
            <div className="mb-4">
              <h3 className="font-medium mb-2">{t('gameHub.rewards')}</h3>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-blue-50 p-2 rounded-lg text-center">
                  <div className="text-blue-600 font-bold">{rewards.daily}</div>
                  <div className="text-xs text-gray-600">{t('gameHub.rewards.daily')}</div>
                </div>
                <div className="bg-green-50 p-2 rounded-lg text-center">
                  <div className="text-green-600 font-bold">{rewards.completion}</div>
                  <div className="text-xs text-gray-600">{t('gameHub.rewards.completion')}</div>
                </div>
                <div className="bg-purple-50 p-2 rounded-lg text-center">
                  <div className="text-purple-600 font-bold">{rewards.tournament}</div>
                  <div className="text-xs text-gray-600">{t('gameHub.rewards.tournament')}</div>
                </div>
              </div>
            </div>
            
            {tournamentActive && (
              <div className="mb-4 bg-yellow-50 p-3 rounded-lg">
                <div className="flex justify-between items-center">
                  <div className="flex items-center">
                    <Trophy size={16} className="text-yellow-600 mr-2" />
                    <span className="font-medium">{t('gameHub.tournament.active')}</span>
                  </div>
                  <span className="text-xs text-yellow-600">
                    {formatTournamentEndTime(tournamentEnds)}
                  </span>
                </div>
                <div className="mt-2 text-sm text-gray-600">
                  {t('gameHub.tournament.joinAndWin', { coins: rewards.tournament })}
                </div>
              </div>
            )}
            
            <div className="flex space-x-3 mt-6">
              <button 
                className="flex-1 py-2 border border-gray-300 rounded-lg"
                onClick={onClose}
              >
                {t('common.cancel')}
              </button>
              <button 
                className="flex-1 py-2 bg-indigo-600 text-white rounded-lg flex items-center justify-center"
                onClick={() => {
                  onClose();
                  // Burada oyunu başlatma işlemi yapılacak
                  alert(t('gameHub.startingGame', { title }));
                }}
              >
                <Gamepad2 size={18} className="mr-1" />
                {t('gameHub.play')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Yükleme ekranı
  if (isLoading) {
    return (
      <div className="flex flex-col h-screen bg-gray-100">
        <header className="bg-white p-4 shadow">
          <h1 className="font-bold text-lg flex items-center">
            <Gamepad2 size={20} className="mr-2 text-indigo-600" />
            {t('screens.gameHubTitle')}
          </h1>
        </header>
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
            <p className="mt-4 text-gray-600">{t('gameHub.loadingGames')}</p>
          </div>
        </div>
        <BottomNavigation activeItem="gameHub" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-indigo-600 p-4 shadow text-white">
        <div className="flex justify-between items-center">
          <h1 className="font-bold text-lg flex items-center">
            <Gamepad2 size={20} className="mr-2" />
            {t('screens.gameHubTitle')}
          </h1>
          
          <div className="flex items-center space-x-2">
            <div className="flex items-center bg-white/20 rounded-full px-2 py-1">
              <Star size={16} className="text-yellow-300 mr-1" />
              <span className="text-sm font-medium">{t('gameHub.level', { level: userStats.level })}</span>
            </div>
            
            <div className="flex items-center bg-white/20 rounded-full px-2 py-1">
              <Coins size={16} className="text-yellow-300 mr-1" />
              <span className="text-sm font-medium">{userStats.tokens}</span>
            </div>
          </div>
        </div>
      </header>

      {/* XP Progress Bar */}
      <div className="bg-indigo-700 px-4 py-2 text-white">
        <div className="flex justify-between text-xs mb-1">
          <span>{t('gameHub.xpProgress', { current: userStats.xp, next: userStats.nextLevelXp })}</span>
          <span>{Math.floor((userStats.xp / userStats.nextLevelXp) * 100)}%</span>
        </div>
        <div className="w-full h-2 bg-indigo-900 rounded-full overflow-hidden">
          <div 
            className="h-full bg-yellow-400 rounded-full" 
            style={{ width: `${(userStats.xp / userStats.nextLevelXp) * 100}%` }}
          ></div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b">
        <div className="flex">
          <button 
            className={`flex-1 py-3 text-sm font-medium ${activeTab === 'games' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-600'}`}
            onClick={() => setActiveTab('games')}
          >
            {t('gameHub.tabs.games')}
          </button>
          <button 
            className={`flex-1 py-3 text-sm font-medium ${activeTab === 'daily' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-600'}`}
            onClick={() => setActiveTab('daily')}
          >
            {t('gameHub.tabs.daily')}
          </button>
          <button 
            className={`flex-1 py-3 text-sm font-medium ${activeTab === 'leaderboard' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-600'}`}
            onClick={() => setActiveTab('leaderboard')}
          >
            {t('gameHub.tabs.leaderboard')}
          </button>
          <button 
            className={`flex-1 py-3 text-sm font-medium ${activeTab === 'rewards' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-600'}`}
            onClick={() => setActiveTab('rewards')}
          >
            {t('gameHub.tabs.rewards')}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 overflow-auto pb-navigation">
        {activeTab === 'games' && (
          <div className="p-4 space-y-4">
            {/* Featured Game */}
            <div className="bg-gradient-to-r from-indigo-600 to-indigo-800 text-white rounded-xl p-4 shadow">
              <div className="mb-2 flex justify-between items-start">
                <h2 className="font-bold text-lg">{t('gameHub.featuredGame')}</h2>
                <div className="bg-white/20 rounded-full px-2 py-0.5 text-xs flex items-center">
                  <Trophy size={12} className="mr-1 text-yellow-300" />
                  {t('gameHub.tournament.active')}
                </div>
              </div>
              <h3 className="text-xl font-bold mb-1">{t('gameHub.games.cryptoQuest.title')}</h3>
              <p className="text-sm text-white/80 mb-3">{t('gameHub.games.cryptoQuest.description')}</p>
              <div className="flex justify-between items-center">
                <div className="space-y-1">
                  <div className="flex items-center text-xs">
                    <Users size={12} className="mr-1" />
                    <span>{t('gameHub.playerCount', { count: 3245 })}</span>
                  </div>
                  <div className="flex items-center text-xs">
                    <Coins size={12} className="mr-1 text-yellow-300" />
                    <span>{t('gameHub.coinReward', { amount: 500 })}</span>
                  </div>
                </div>
                <button 
                  className="bg-white text-indigo-700 px-4 py-2 rounded-lg font-medium text-sm"
                  onClick={() => handlePlayGame(1)}
                >
                  {t('gameHub.play')}
                </button>
              </div>
            </div>
            
            {/* Game Categories */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <h2 className="font-bold">{t('gameHub.categoriesTitle')}</h2>
                <button className="text-indigo-600 text-sm font-medium flex items-center">
                  {t('gameHub.seeAll')}
                  <ChevronRight size={16} className="ml-1" />
                </button>
              </div>
              <div className="grid grid-cols-5 gap-2">
                <div className="bg-blue-100 p-2 rounded-lg text-center">
                  <div className="bg-blue-200 rounded-full w-10 h-10 flex items-center justify-center mx-auto mb-1">
                    <svg xmlns="http://www.w3.org/2000/svg" className="text-blue-600" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>
                  </div>
                  <span className="text-xs font-medium text-blue-800">{t('gameHub.categories.quiz')}</span>
                </div>
                <div className="bg-purple-100 p-2 rounded-lg text-center">
                  <div className="bg-purple-200 rounded-full w-10 h-10 flex items-center justify-center mx-auto mb-1">
                    <svg xmlns="http://www.w3.org/2000/svg" className="text-purple-600" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4a7 7 0 0 0 0 14h7a3 3 0 0 0 0-6h-1a2 2 0 0 1 0-4h1a3 3 0 0 0 0-6h-7z"></path><path d="M15.5 12.5 17 14l3-3"></path></svg>
                  </div>
                  <span className="text-xs font-medium text-purple-800">{t('gameHub.categories.puzzle')}</span>
                </div>
                <div className="bg-yellow-100 p-2 rounded-lg text-center">
                  <div className="bg-yellow-200 rounded-full w-10 h-10 flex items-center justify-center mx-auto mb-1">
                    <svg xmlns="http://www.w3.org/2000/svg" className="text-yellow-600" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>
                  </div>
                  <span className="text-xs font-medium text-yellow-800">{t('gameHub.categories.collection')}</span>
                </div>
                <div className="bg-green-100 p-2 rounded-lg text-center">
                  <div className="bg-green-200 rounded-full w-10 h-10 flex items-center justify-center mx-auto mb-1">
                    <svg xmlns="http://www.w3.org/2000/svg" className="text-green-600" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m2 2l20 20"></path><path d="M8.5 8.5c1.7-1.7 4.3-1.7 6 0 1.7 1.7 1.7 4.3 0 6-1.7 1.7-4.3 1.7-6 0-1.7-1.7-1.7-4.3 0-6Z"></path><path d="M14.5 9.5c1.7-1.7 4.3-1.7 6 0 1.7 1.7 1.7 4.3 0 6-1.7 1.7-4.3 1.7-6 0"></path><path d="M3.5 14.5c-1.7 1.7-1.7 4.3 0 6 1.7 1.7 4.3 1.7 6 0 1.7-1.7 1.7-4.3 0-6"></path><path d="M9.5 3.5c-1.7-1.7-4.3-1.7-6 0-1.7 1.7-1.7 4.3 0 6"></path></svg>
                  </div>
                  <span className="text-xs font-medium text-green-800">{t('gameHub.categories.simulation')}</span>
                </div>
                <div className="bg-red-100 p-2 rounded-lg text-center">
                  <div className="bg-red-200 rounded-full w-10 h-10 flex items-center justify-center mx-auto mb-1">
                    <svg xmlns="http://www.w3.org/2000/svg" className="text-red-600" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 19H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-4"></path><path d="M9 14.5 4 9l5-5"></path><path d="M14 8v3h-8"></path></svg>
                  </div>
                  <span className="text-xs font-medium text-red-800">{t('gameHub.categories.strategy')}</span>
                </div>
              </div>
            </div>
            
            {/* All Games */}
            <div>
              <h2 className="font-bold mb-3">{t('gameHub.allGames')}</h2>
              <div className="space-y-3">
                {games.map(game => (
                  <div 
                    key={game.id}
                    className="bg-white rounded-lg shadow p-3 flex items-center"
                    onClick={() => handlePlayGame(game.id)}
                  >
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center mr-3 ${
                      game.category === 'quiz' ? 'bg-blue-100 text-blue-600' :
                      game.category === 'puzzle' ? 'bg-purple-100 text-purple-600' :
                      game.category === 'collection' ? 'bg-yellow-100 text-yellow-600' :
                      game.category === 'simulation' ? 'bg-green-100 text-green-600' :
                      'bg-red-100 text-red-600'
                    }`}>
                      {getCategoryIcon(game.category)}
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-medium">{game.title}</h3>
                          <div className="flex items-center text-xs text-gray-500 mt-0.5">
                            <span className="mr-2">{getDifficultyIcon(game.difficulty)}</span>
                            <span className="flex items-center">
                              <Users size={12} className="mr-1" />
                              {game.players.toLocaleString()}
                            </span>
                          </div>
                        </div>
                        {game.tournamentActive && (
                          <div className="bg-yellow-100 text-yellow-800 text-xs px-1.5 py-0.5 rounded flex items-center">
                            <Trophy size={10} className="mr-0.5" />
                            {t('gameHub.tournament.short')}
                          </div>
                        )}
                      </div>
                      
                      <div className="mt-1">
                        <div className="flex justify-between text-xs mb-0.5">
                          <span>{t('gameHub.level', { completed: game.completedLevels, total: game.totalLevels })}</span>
                          <span>{game.userProgress}%</span>
                        </div>
                        <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-indigo-500 rounded-full" 
                            style={{ width: `${game.userProgress}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                    
                    <button className="ml-2 bg-indigo-600 text-white px-3 py-1.5 rounded text-sm font-medium">
                      {t('gameHub.play')}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'daily' && (
          <div className="p-4 space-y-4">
            {/* Daily Streak */}
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex justify-between items-center mb-3">
                <h2 className="font-bold">{t('gameHub.dailyLogin')}</h2>
                <div className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full text-xs flex items-center">
                  <Zap size={12} className="mr-1" />
                  {t('gameHub.dayStreak', { days: userStats.dailyStreak })}
                </div>
              </div>
              <div className="flex justify-between mb-2">
                {Array.from({ length: 7 }, (_, i) => (
                  <div 
                    key={i} 
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      i < userStats.dailyStreak ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-400'
                    }`}
                  >
                    {i < userStats.dailyStreak ? (
                      <Check size={16} />
                    ) : (
                      <span>{i + 1}</span>
                    )}
                  </div>
                ))}
              </div>
              <div className="text-sm text-gray-600">
                {t('gameHub.dailyLoginInfo')}
              </div>
            </div>
            
            {/* Daily Tasks */}
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex justify-between items-center mb-3">
                <h2 className="font-bold">{t('gameHub.dailyTasks.title')}</h2>
                <div className="text-xs text-gray-500">
                  {t('gameHub.completed', { completed: dailyTasks.filter(t => t.isCompleted).length, total: dailyTasks.length })}
                </div>
              </div>
              <div className="space-y-3">
                {dailyTasks.map(task => (
                  <div 
                    key={task.id} 
                    className={`flex items-center p-3 rounded-lg border ${
                      task.isCompleted ? 'border-green-200 bg-green-50' : 'border-gray-200'
                    }`}
                  >
                    <button
                      className={`mr-3 w-6 h-6 rounded-full border flex items-center justify-center ${
                        task.isCompleted 
                          ? 'bg-green-100 border-green-500 text-green-500' 
                          : 'border-gray-300'
                      }`}
                      onClick={() => toggleTaskComplete(task.id)}
                    >
                      {task.isCompleted && <Check size={14} />}
                    </button>
                    
                    <div className="flex-1">
                      <p className={task.isCompleted ? 'line-through text-gray-500' : ''}>
                        {task.title}
                      </p>
                    </div>
                    
                    <div className="flex items-center ml-2 bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded text-sm">
                      <Coins size={14} className="mr-1 text-yellow-500" />
                      {task.reward}
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-4 bg-yellow-50 border border-yellow-200 p-3 rounded-lg">
                <div className="flex items-center text-yellow-800">
                  <Gift size={18} className="mr-2 text-yellow-600" />
                  <span className="font-medium">{t('gameHub.dailyTasks.completeAll')}</span>
                </div>
                <div className="text-sm text-yellow-700 mt-1">
                  {t('gameHub.dailyTasks.completeAllBonus', { coins: 25 })}
                </div>
              </div>
            </div>
            
            {/* Active Tournaments */}
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex justify-between items-center mb-3">
                <h2 className="font-bold">{t('gameHub.tournament.activeTournaments')}</h2>
                <button className="text-indigo-600 text-sm font-medium flex items-center">
                  {t('gameHub.seeAll')}
                  <ChevronRight size={16} className="ml-1" />
                </button>
              </div>
              <div className="space-y-3">
                {games.filter(g => g.tournamentActive).map(game => (
                  <div key={game.id} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-medium">{t('gameHub.tournament.gameTournament', { game: game.title })}</h3>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {formatTournamentEndTime(game.tournamentEnds)}
                        </div>
                      </div>
                      <div className="flex items-center bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded text-xs">
                        <Trophy size={12} className="mr-1" />
                        {t('gameHub.coinReward', { amount: game.rewards.tournament })}
                      </div>
                    </div>
                    <div className="mt-2 flex justify-between">
                      <span className="text-xs text-gray-500">
                        {t('gameHub.participants', { count: Math.floor(game.players * 0.4).toLocaleString() })}
                      </span>
                      <button 
                        className="text-indigo-600 text-sm font-medium"
                        onClick={() => handlePlayGame(game.id)}
                      >
                        {t('gameHub.join')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'leaderboard' && (
          <div className="p-4 space-y-4">
            {/* User Rank Info */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg p-4 shadow">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-sm text-white/80">{t('gameHub.leaderboard.yourRank')}</h2>
                  <div className="flex items-center">
                    <Trophy size={16} className="mr-1 text-yellow-300" />
                    <span className="text-xl font-bold">{t('gameHub.leaderboard.rankPosition', { rank: userStats.rank })}</span>
                  </div>
                  <div className="text-sm mt-1">
                    {t('gameHub.leaderboard.topPercentage', { percent: Math.floor((userStats.rank / userStats.totalUsers) * 100) })}
                  </div>
                </div>
                <div className="bg-white/20 rounded-lg p-2 flex flex-col items-center">
                  <div className="text-2xl font-bold">{userStats.xp}</div>
                  <div className="text-xs">{t('gameHub.totalPoints')}</div>
                </div>
              </div>
            </div>
            
            {/* Weekly and All-Time Tabs */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="flex">
                <button className="flex-1 py-2 border-b-2 border-indigo-600 text-indigo-600 font-medium">
                  {t('gameHub.leaderboard.weekly')}
                </button>
                <button className="flex-1 py-2 text-gray-500">
                  {t('gameHub.leaderboard.allTime')}
                </button>
              </div>
              
              <div className="p-2 space-y-1">
                {leaderboard.map((user, index) => (
                  <div 
                    key={index}
                    className={`flex items-center p-2 rounded ${user.isCurrentUser ? 'bg-indigo-50' : ''}`}
                  >
                    <div className={`w-8 h-8 flex items-center justify-center rounded-full mr-3 ${
                      user.rank <= 3 
                        ? `text-white ${
                            user.rank === 1 ? 'bg-yellow-500' : 
                            user.rank === 2 ? 'bg-gray-400' : 
                            'bg-amber-700'}`
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {user.rank}
                    </div>
                    
                    <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center mr-3">
                      {user.avatar ? (
                        <img 
                          src={user.avatar} 
                          alt={user.username} 
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        <span className="text-indigo-600 font-medium">
                          {user.username.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center">
                        <span className={`font-medium ${user.isCurrentUser ? 'text-indigo-700' : ''}`}>
                          {user.username}
                        </span>
                        {user.isCurrentUser && (
                          <span className="ml-2 bg-indigo-600 text-white text-xs px-2 py-0.5 rounded">
                            {t('gameHub.leaderboard.you')}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center font-medium text-gray-700">
                      <Star size={16} className="mr-1 text-yellow-500" />
                      {user.points.toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="p-4 border-t">
                <button className="w-full py-2 border border-indigo-600 text-indigo-600 rounded-lg font-medium">
                  {t('gameHub.leaderboard.seeFullList')}
                </button>
              </div>
            </div>
            
            {/* Game-specific Leaderboards */}
            <div>
              <h2 className="font-bold mb-3">{t('gameHub.leaderboard.gameLeaderboards')}</h2>
              <div className="space-y-3">
                {games.slice(0, 3).map(game => (
                  <div key={game.id} className="bg-white rounded-lg shadow p-3">
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="font-medium">{game.title}</h3>
                      <ChevronRight size={18} className="text-gray-400" />
                    </div>
                    <div className="flex justify-between text-sm">
                      <div className="flex items-center">
                        <div className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center mr-1">
                          <Trophy size={12} className="text-yellow-600" />
                        </div>
                        <span>cryptomaster</span>
                      </div>
                      <div className="text-gray-500">{(game.players * 18 + game.id * 100).toLocaleString()} {t('gameHub.points')}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'rewards' && (
          <div className="p-4 space-y-4">
            {/* User Tokens */}
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center mb-3">
                <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mr-3">
                  <Coins size={24} className="text-indigo-600" />
                </div>
                <div>
                  <div className="text-sm text-gray-500">{t('gameHub.rewards.balance')}</div>
                  <div className="text-2xl font-bold">{userStats.tokens} <span className="text-indigo-600">IDCOIN</span></div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button className="bg-indigo-600 text-white py-2 rounded-lg font-medium text-sm flex items-center justify-center">
                  <ArrowUp size={16} className="mr-1" />
                  {t('gameHub.rewards.transfer')}
                </button>
                <button className="border border-indigo-600 text-indigo-600 py-2 rounded-lg font-medium text-sm flex items-center justify-center">
                  <BarChart size={16} className="mr-1" />
                  {t('gameHub.rewards.transactions')}
                </button>
              </div>
            </div>
            
            {/* Achievements */}
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex justify-between items-center mb-3">
                <h2 className="font-bold">{t('gameHub.achievements.title')}</h2>
                <div className="text-xs text-gray-500">
                  {t('gameHub.completed', { completed: userStats.achievements, total: 50 })}
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
                    <Award size={20} className="text-green-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="font-medium">{t('gameHub.achievements.firstGame')}</h3>
                        <p className="text-xs text-gray-500">{t('gameHub.achievements.firstGameDesc')}</p>
                      </div>
                      <div className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs">
                        {t('gameHub.achievements.completed')}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center mr-3">
                    <Trophy size={20} className="text-yellow-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="font-medium">{t('gameHub.achievements.firstTournament')}</h3>
                        <p className="text-xs text-gray-500">{t('gameHub.achievements.firstTournamentDesc')}</p>
                      </div>
                      <div className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs">
                        {t('gameHub.achievements.completed')}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    <Zap size={20} className="text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="font-medium">{t('gameHub.achievements.gameMaster')}</h3>
                        <p className="text-xs text-gray-500">{t('gameHub.achievements.gameMasterDesc')}</p>
                      </div>
                      <div className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs">
                        3/5
                      </div>
                    </div>
                    <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden mt-1">
                      <div className="h-full bg-blue-500 rounded-full" style={{ width: '60%' }}></div>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                    <Gem size={20} className="text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="font-medium">{t('gameHub.achievements.collector')}</h3>
                        <p className="text-xs text-gray-500">{t('gameHub.achievements.collectorDesc')}</p>
                      </div>
                      <div className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded text-xs">
                        7/10
                      </div>
                    </div>
                    <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden mt-1">
                      <div className="h-full bg-purple-500 rounded-full" style={{ width: '70%' }}></div>
                    </div>
                  </div>
                </div>
              </div>
              
              <button className="w-full mt-4 py-2 border border-indigo-600 text-indigo-600 rounded-lg font-medium">
                {t('gameHub.achievements.seeAll')}
              </button>
            </div>
            
            {/* Rewards Shop */}
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex justify-between items-center mb-3">
                <h2 className="font-bold">{t('gameHub.rewards.shop')}</h2>
                <button className="text-indigo-600 text-sm font-medium flex items-center">
                  {t('gameHub.seeAll')}
                  <ChevronRight size={16} className="ml-1" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="border border-gray-200 rounded-lg p-3">
                  <div className="bg-blue-100 rounded-lg p-3 flex items-center justify-center mb-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-600"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
                  </div>
                  <h3 className="font-medium text-center">{t('gameHub.rewards.items.premium.title')}</h3>
                  <div className="text-center text-sm text-gray-500 mb-2">{t('gameHub.rewards.items.premium.description')}</div>
                  <button className="w-full bg-indigo-600 text-white py-1.5 rounded text-sm font-medium flex items-center justify-center">
                    <Coins size={14} className="mr-1" />
                    {t('gameHub.rewards.cost', { amount: 200 })}
                  </button>
                </div>
                
                <div className="border border-gray-200 rounded-lg p-3">
                  <div className="bg-purple-100 rounded-lg p-3 flex items-center justify-center mb-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-purple-600"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><polyline points="3.29 7 12 12 20.71 7" /><line x1="12" y1="22" x2="12" y2="12" /></svg>
                  </div>
                  <h3 className="font-medium text-center">{t('gameHub.rewards.items.nft.title')}</h3>
                  <div className="text-center text-sm text-gray-500 mb-2">{t('gameHub.rewards.items.nft.description')}</div>
                  <button className="w-full bg-indigo-600 text-white py-1.5 rounded text-sm font-medium flex items-center justify-center">
                    <Coins size={14} className="mr-1" />
                    {t('gameHub.rewards.cost', { amount: 150 })}
                  </button>
                </div>
                
                <div className="border border-gray-200 rounded-lg p-3">
                  <div className="bg-green-100 rounded-lg p-3 flex items-center justify-center mb-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-600"><circle cx="12" cy="12" r="10" /><path d="m8 14 2.5 2.5L16 10" /></svg>
                  </div>
                  <h3 className="font-medium text-center">{t('gameHub.rewards.items.xpBoost.title')}</h3>
                  <div className="text-center text-sm text-gray-500 mb-2">{t('gameHub.rewards.items.xpBoost.description')}</div>
                  <button className="w-full bg-indigo-600 text-white py-1.5 rounded text-sm font-medium flex items-center justify-center">
                    <Coins size={14} className="mr-1" />
                    {t('gameHub.rewards.cost', { amount: 80 })}
                  </button>
                </div>
                
                <div className="border border-gray-200 rounded-lg p-3">
                  <div className="bg-yellow-100 rounded-lg p-3 flex items-center justify-center mb-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-yellow-600"><path d="M6 19v-3"></path><path d="M10 19v-3"></path><path d="M14 19v-3"></path><path d="M18 19v-3"></path><path d="M8 11V9"></path><path d="M16 11V9"></path><path d="M12 11V9"></path><path d="M2 15h20"></path><path d="M2 7a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v1.1a2 2 0 0 0 0 3.837V17a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-5.1a2 2 0 0 0 0-3.837V7Z"></path></svg>
                  </div>
                  <h3 className="font-medium text-center">{t('gameHub.rewards.items.cryptoGift.title')}</h3>
                  <div className="text-center text-sm text-gray-500 mb-2">{t('gameHub.rewards.items.cryptoGift.description')}</div>
                  <button className="w-full bg-indigo-600 text-white py-1.5 rounded text-sm font-medium flex items-center justify-center">
                    <Coins size={14} className="mr-1" />
                    {t('gameHub.rewards.cost', { amount: 500 })}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Bottom Navigation */}
      <BottomNavigation activeItem="gameHub" />
      
      {/* Game Detail Modal */}
      {showGameDetail && (
        <GameDetailModal 
          game={showGameDetail} 
          onClose={() => setShowGameDetail(null)}
        />
      )}
    </div>
  );
};

export default GameHubScreen;